import type { PreviewColumn, PreviewRowFieldError } from '../state/api/organisationEngineSlice';

export type CellOverlay = {
    /** Whether to draw the bad-cell border on this column. */
    bordered: boolean;
    /** Messages to render beneath this column's cell. May be empty. */
    messages: string[];
};

/**
 * Builds the per-column overlay that the preview table uses to draw bad-cell
 * borders and inline error messages. The column-index → cell mapping is
 * derived from the server-sent `columns` list; the engine's error `field`
 * tags must match a column `key` for the overlay to fire.
 *
 * Single-field errors anchor both border and message to their own column.
 * The engine's `coordinates` pseudo-field (when both lat AND lng are blank)
 * borders both axes but anchors the message under latitude only — avoids
 * showing the same "Coordinates are required" line twice in adjacent cells.
 * Unknown field names are skipped — defensive against engine additions we
 * haven't seen yet.
 */
export const buildCellErrorOverlay = (errors: PreviewRowFieldError[], columns: PreviewColumn[]): Record<number, CellOverlay> => {
    const indexByKey = new Map(columns.map((col, idx) => [col.key, idx]));
    const overlay: Record<number, CellOverlay> = {};

    const upsert = (columnIndex: number, message: string | null) => {
        const existing = overlay[columnIndex] ?? { bordered: true, messages: [] };
        if (message !== null) existing.messages.push(message);
        existing.bordered = true;
        overlay[columnIndex] = existing;
    };

    const latitudeCol = indexByKey.get('latitude');
    const longitudeCol = indexByKey.get('longitude');

    for (const err of errors) {
        if (err.field === 'coordinates') {
            if (latitudeCol !== undefined) upsert(latitudeCol, err.message);
            if (longitudeCol !== undefined) upsert(longitudeCol, null);
            continue;
        }
        const columnIndex = indexByKey.get(err.field);
        if (columnIndex === undefined) continue;
        upsert(columnIndex, err.message);
    }

    return overlay;
};
